import { withDemoContacts } from './demo-contacts';
import { type Snapshot, type Command, parseSnapshot } from './models';
import { taskSchema } from './ai';
import { applyCommand } from './commands';
import { CURRENT_USER, seedSnapshot, newFounderSnapshot } from './seed';
import { profileSchema } from './models';
export interface RecordStorage {
  load(): Promise<Snapshot | null>;
  save(snapshot: Snapshot): Promise<void>;
}
export interface AppRepository {
  read(): Promise<Snapshot>;
  execute(command: Command): Promise<Snapshot>;
  reset(): Promise<Snapshot>;
  mode: 'local' | 'server';
}
export interface CalendarService {
  list(ventureId: string): Promise<Snapshot['events']>;
}
export interface ChatService {
  list(channel: string): Promise<Snapshot['messages']>;
  send(channel: string, text: string): Promise<void>;
}
export class LocalRepository implements AppRepository {
  readonly mode = 'local' as const;
  private queue: Promise<unknown> = Promise.resolve();
  constructor(private storage: RecordStorage) {}
  async startProfile(input: unknown) {
    await this.queue;
    const next = newFounderSnapshot(profileSchema.parse(input));
    await this.storage.save(next);
    return next;
  }
  async read() {
    const saved = await this.storage.load();
    if (saved) {
      const parsed = parseSnapshot(saved);
      const upgraded = withDemoContacts(parsed);
      if (upgraded !== parsed) await this.storage.save(upgraded);
      return upgraded;
    }
    const seed = seedSnapshot();
    await this.storage.save(seed);
    return seed;
  }
  execute(command: Command): Promise<Snapshot> {
    const operation = this.queue.then(async () => {
      const next = applyCommand(await this.read(), command, CURRENT_USER);

      await this.storage.save(next);
      return next;
    });
    this.queue = operation.catch(() => undefined);
    return operation;
  }
  async reset() {
    await this.queue;
    const seed = seedSnapshot();
    await this.storage.save(seed);
    return seed;
  }
}
export class HttpRepository implements AppRepository {
  readonly mode = 'server' as const;
  constructor(
    private url: string,
    private token: string,
  ) {
    const u = new URL(url);
    if (
      u.protocol !== 'https:' &&
      !(u.protocol === 'http:' && ['127.0.0.1', 'localhost', '10.0.2.2'].includes(u.hostname))
    )
      throw Error('Use HTTPS, or a local development address.');
  }
  private async request(path: string, body?: unknown) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(`${this.url.replace(/\/$/, '')}${path}`, {
        method: body ? 'POST' : 'GET',
        credentials: 'include',
        headers: {
          ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
          ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });
      const data: unknown = await response.json();
      if (!response.ok)
        throw Error(
          typeof data === 'object' && data && 'error' in data
            ? String(data.error)
            : 'The backend could not complete this request.',
        );
      return parseSnapshot(data);
    } finally {
      clearTimeout(timeout);
    }
  }
  read() {
    return this.request('/api/state');
  }
  execute(command: Command) {
    return this.request('/api/commands', { command });
  }
  private async api(path: string, method = 'GET') {
    const response = await fetch(`${this.url.replace(/\/$/, '')}${path}`, {
      method,
      credentials: 'include',
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
      signal: AbortSignal.timeout(12000),
    });
    if (!response.ok) throw Error('Backend request failed. Check your connection and permissions.');
    return response;
  }
  async identity(): Promise<{ userId: string }> {
    return (await this.api('/api/identity')).json();
  }
  async tasks() {
    const data: unknown = await (await this.api('/api/ai/tasks')).json();
    return taskSchema.array().parse(data);
  }
  async cancelTask(id: string) {
    await this.api(`/api/ai/tasks/${encodeURIComponent(id)}/cancel`, 'POST');
  }
  async artifact(id: string) {
    return new Uint8Array(
      await (await this.api(`/api/artifacts/${encodeURIComponent(id)}/download`)).arrayBuffer(),
    );
  }
  async reset(): Promise<Snapshot> {
    throw Error('Reset is available in offline demo mode only.');
  }
}
